import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  IconButton,
  ListItemText,
  makeStyles,
  Paper,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  Fade,
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import GetAppIcon from '@material-ui/icons/GetApp';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import PageviewIcon from '@material-ui/icons/Pageview';
import clsx from 'clsx';
import fileSize from 'filesize';

import InfiniteScroll from 'components/InfiniteScroll';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  atualizarDocumentosAtendimento,
  atualizarDocumentosGrupo,
  AtualizarMidiaMensagemInterface,
  baixandoMensagemDocumentosAtendimento,
  baixandoMensagemDocumentosGrupo,
  BaixandoMidiaMensagemInterface,
  fetchDocumentosAtendimento,
  fetchDocumentosGrupo,
  MensagemInterface,
  mensagensSelector,
  ParamsPage,
  resetGalerias,
} from 'store/reducers/mensagens';

import ButtonDownloadMidia from './ButtonDownloadMidia';

type TabPanelDocumentosProps = {
  atendimentoId?: string;
  grupoChatId?: string;
  isGroup?: boolean;
};

const useStyles = makeStyles(({ spacing, shadows, palette }) => ({
  list: {
    height: '100%',
    overflow: 'auto',
    padding: spacing(0, 2),
  },
  paper: {
    padding: spacing(1, 2),
    margin: spacing(1) / 2,
    cursor: 'pointer',
    border: `2px solid transparent`,
    backgroundColor: palette.background.tertiary,

    '&:hover': {
      boxShadow: shadows[4],
    },
  },
  activeDocument: {
    border: `2px solid ${palette.primary.main}`,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export default function TabPanelDocumentos(props: TabPanelDocumentosProps) {
  const { atendimentoId, grupoChatId, isGroup = false } = props;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const {
    // Documentos [atendimento]
    documentosAtendimento,
    documentosAtendimentoInfoPage,
    loadingDocumentosAtendimento,
    // Documentos [grupos]
    documentosGrupo,
    documentosGrupoInfoPage,
    loadingDocumentosGrupo,
  } = useAppSelector(mensagensSelector);
  const [activeDocument, setActiveDocument] = useState<MensagemInterface>();
  const [initialParamsPage] = useState<ParamsPage>({ page: 1, limit: 50 });

  // Lista de documentos do [atendimento]
  const getDocumentosAtendimento = useCallback(
    (params: { atendimentoId: string; params: ParamsPage }) => {
      dispatch(fetchDocumentosAtendimento(params));
    },
    [dispatch],
  );

  // Lista de documentos do [grupo]
  const getDocumentosGrupo = useCallback(
    (params: { grupoChatId: string; params: ParamsPage }) => {
      dispatch(fetchDocumentosGrupo(params));
    },
    [dispatch],
  );

  useEffect(() => {
    if (!isGroup && !grupoChatId && atendimentoId) {
      getDocumentosAtendimento({ atendimentoId, params: initialParamsPage });
    } else if (grupoChatId) {
      getDocumentosGrupo({ grupoChatId, params: initialParamsPage });
    }

    return () => {
      dispatch(resetGalerias());
    };
  }, [
    atendimentoId,
    dispatch,
    getDocumentosAtendimento,
    getDocumentosGrupo,
    grupoChatId,
    initialParamsPage,
    isGroup,
  ]);

  // Carrega os próximos documentos do [atendimento]
  const fetchNextDocumentosAtendimento = () => {
    const { nextPage } = documentosAtendimentoInfoPage;
    const checkNextPage = nextPage !== null && nextPage !== undefined;
    if (!loadingDocumentosAtendimento && checkNextPage) {
      const params: ParamsPage = { page: nextPage, limit: 50 };
      if (atendimentoId) getDocumentosAtendimento({ atendimentoId, params });
    }
  };

  // Carrega os próximos documentos do [grupo]
  const fetchNextDocumentosGrupo = () => {
    const { nextPage } = documentosGrupoInfoPage;
    const checkNextPage = nextPage !== null && nextPage !== undefined;
    if (!loadingDocumentosGrupo && checkNextPage) {
      const params: ParamsPage = { page: nextPage, limit: 50 };
      if (grupoChatId) getDocumentosGrupo({ grupoChatId, params });
    }
  };

  useEffect(() => {
    const onBaixandoMidiaMensagem = (data: BaixandoMidiaMensagemInterface) => {
      if (atendimentoId) dispatch(baixandoMensagemDocumentosAtendimento(data));
    };
    const onAtualizarMidiaMensagem = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      if (atendimentoId) dispatch(atualizarDocumentosAtendimento(data));
    };

    socket.on(socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM, onBaixandoMidiaMensagem);
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
      onAtualizarMidiaMensagem,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
        onBaixandoMidiaMensagem,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );
    };
  }, [atendimentoId, dispatch, grupoChatId]);

  useEffect(() => {
    const baixandoMidiaMensagemGrupo = (
      data: BaixandoMidiaMensagemInterface,
    ) => {
      if (grupoChatId) dispatch(baixandoMensagemDocumentosGrupo(data));
    };

    const atualizarMidiaMensagemGrupo = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      if (grupoChatId) dispatch(atualizarDocumentosGrupo(data));
    };

    socket.on(
      socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM_GRUPO,
      baixandoMidiaMensagemGrupo,
    );
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM_GRUPO,
      atualizarMidiaMensagemGrupo,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM_GRUPO,
        baixandoMidiaMensagemGrupo,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM_GRUPO,
        atualizarMidiaMensagemGrupo,
      );
    };
  }, [dispatch, grupoChatId]);

  const renderDocumento = (midia: MensagemInterface) => {
    const {
      id,
      midiaDownloadErro,
      midiaTitulo,
      midiaTamanho,
      midiaUrl,
      atendimentoId,
      grupoChatId,
    } = midia;
    return (
      <Fade key={midia.id} in unmountOnExit mountOnEnter timeout={800}>
        <Paper
          elevation={1}
          onClick={() => setActiveDocument(midia)}
          className={clsx(classes.paper, {
            [classes.activeDocument]:
              activeDocument?.id === id && !midiaDownloadErro,
          })}
        >
          <ListItem disabled={midiaDownloadErro} className={classes.content}>
            {midiaDownloadErro ? (
              <ListItemAvatar>
                <ErrorIcon
                  titleAccess="Erro ao carregar documento"
                  fontSize="large"
                />
              </ListItemAvatar>
            ) : (
              <ListItemAvatar>
                <InsertDriveFileIcon fontSize="large" />
              </ListItemAvatar>
            )}

            <ListItemText
              title={midiaTitulo}
              primary={midiaTitulo || 'Documento'}
              primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              secondary={`Tamanho - ${
                fileSize(midiaTamanho, { round: 0, locale: 'pt-BR' }) || 0
              } `}
              secondaryTypographyProps={{ noWrap: true, variant: 'caption' }}
            />

            {!midiaDownloadErro && (
              <>
                <Box ml={2}>
                  <Link
                    rel="noopener noreferrer"
                    target="_blank"
                    href={midiaUrl}
                  >
                    <IconButton title="Visualizar">
                      <PageviewIcon />
                    </IconButton>
                  </Link>
                </Box>
                <Box ml={1}>
                  <Link
                    rel="noopener noreferrer"
                    href={`${midiaUrl}?download=true`}
                  >
                    <IconButton title="Baixar">
                      <GetAppIcon />
                    </IconButton>
                  </Link>
                </Box>
              </>
            )}
            {midiaDownloadErro && (
              <Box ml={3}>
                <ButtonDownloadMidia
                  buttonText="Recarregar documento"
                  mensagemId={id}
                  atendimentoId={atendimentoId}
                  grupoChatId={grupoChatId}
                />
              </Box>
            )}
          </ListItem>
        </Paper>
      </Fade>
    );
  };

  const renderDocumentosAtendimento = () => {
    return (
      <List id="scrollableDocumentosAtendimento" className={classes.list}>
        <InfiniteScroll
          scrollableTarget="scrollableDocumentosAtendimento"
          hasMore={Boolean(documentosAtendimentoInfoPage.nextPage)}
          next={fetchNextDocumentosAtendimento}
          data={documentosAtendimento}
          loading={loadingDocumentosAtendimento}
          empty={{
            title: 'Não há documentos',
            description: 'Nenhum documento foi encontrado neste atendimento!',
          }}
        >
          {documentosAtendimento.map(midia => renderDocumento(midia))}
        </InfiniteScroll>
      </List>
    );
  };

  const renderDocumentosGrupo = () => {
    return (
      <List id="scrollableDocumentosGrupo" className={classes.list}>
        <InfiniteScroll
          scrollableTarget="scrollableDocumentosGrupo"
          hasMore={Boolean(documentosGrupoInfoPage.nextPage)}
          next={fetchNextDocumentosGrupo}
          data={documentosGrupo}
          loading={loadingDocumentosGrupo}
          empty={{
            title: 'Não há documentos',
            description: 'Nenhum documento foi encontrado neste grupo!',
          }}
        >
          {documentosGrupo.map(midia => renderDocumento(midia))}
        </InfiniteScroll>
      </List>
    );
  };

  return !isGroup ? renderDocumentosAtendimento() : renderDocumentosGrupo();
}
